import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import {
  Currency,
  SupportedCurrency,
} from '@sorare/core/src/__generated__/globalTypes';
import { TabContainer } from '@sorare/core/src/atoms/layout/TabContainer';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';

import { MyPage } from '../../MyPage';
import { SpentFiatPaymentIntents } from '../SpentFiatPaymentIntents';
import { UserAccountEntries } from '../UserAccountEntries';

const messages = defineMessages({
  FIAT: {
    id: 'TransactionTabs.fiat',
    defaultMessage: 'Cash Wallet',
  },
  ETH: {
    id: 'PaymentBox.eth',
    defaultMessage: 'ETH Wallet',
  },
  CREDIT_CARD: {
    id: 'TransactionTabs.creditCard',
    defaultMessage: 'Credit card',
  },
  VIP: {
    id: 'TransactionTabs.vip',
    defaultMessage: 'VIP',
  },
  ESSENCE: {
    id: 'TransactionTabs.essence',
    defaultMessage: 'Essence',
  },
  SOL: {
    id: 'TransactionTabs.sol',
    defaultMessage: 'SOL Wallet',
  },
});

type TabValue = 'ETH' | 'SOL' | 'FIAT' | 'CREDIT_CARD';

export const TransactionHistoryCompact = () => {
  const {
    currentUser,
    walletPreferences: { showFiatWallet, showCryptoWallet },
  } = useCurrentUserContext();
  const { cryptocurrency } = useCurrentUserCurrencies();

  const initialTab = (() => {
    if (showCryptoWallet) {
      switch (cryptocurrency) {
        case SupportedCurrency.LAMPORT:
          return 'SOL';
        case SupportedCurrency.WEI:
        default:
          return 'ETH';
      }
    }
    if (showFiatWallet) return 'FIAT';
    return 'CREDIT_CARD';
  })();

  const [tab, setTab] = useState<TabValue>(initialTab);
  const hasSolana = currentUser?.wallet?.solanaAddress;

  return (
    <MyPage>
      <TabBar value={tab} fullWidth variant="flat">
        {!!showCryptoWallet && (
          <>
            {!!hasSolana && (
              <Tab value="SOL" onClick={() => setTab('SOL')}>
                <FormattedMessage {...messages.SOL} />
              </Tab>
            )}
            <Tab value="ETH" onClick={() => setTab('ETH')}>
              <FormattedMessage {...messages.ETH} />
            </Tab>
          </>
        )}
        {!!showFiatWallet && (
          <Tab value="FIAT" onClick={() => setTab('FIAT')}>
            <FormattedMessage {...messages.FIAT} />
          </Tab>
        )}
        <Tab value="CREDIT_CARD" onClick={() => setTab('CREDIT_CARD')}>
          <FormattedMessage {...messages.CREDIT_CARD} />
        </Tab>
      </TabBar>
      <TabContainer>
        {tab === 'SOL' && (
          <UserAccountEntries
            currency={Currency.ETH}
            cryptocurrency={SupportedCurrency.LAMPORT}
            short
          />
        )}
        {tab === 'ETH' && (
          <UserAccountEntries
            currency={Currency.ETH}
            cryptocurrency={SupportedCurrency.WEI}
            short
          />
        )}
        {tab === 'FIAT' && (
          <UserAccountEntries currency={Currency.FIAT} short />
        )}
        {tab === 'CREDIT_CARD' && <SpentFiatPaymentIntents short />}
      </TabContainer>
    </MyPage>
  );
};
